"use client"

import { createContext, useContext, useReducer, useEffect } from "react"

// Define initial state
const initialState = {
  orders: [],
  loading: false,
  error: null,
}

// Create context
const OrderContext = createContext(initialState)

// Order reducer
const orderReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_ORDERS_REQUEST":
      return {
        ...state,
        loading: true,
        error: null,
      }
    case "FETCH_ORDERS_SUCCESS":
      return {
        ...state,
        orders: action.payload,
        loading: false,
      }
    case "FETCH_ORDERS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      }
    case "ADD_ORDER":
      return {
        ...state,
        orders: [action.payload, ...state.orders],
      }
    default:
      return state
  }
}

// Provider component
export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState)

  // Load orders from localStorage on initial render
  useEffect(() => {
    const savedOrders = localStorage.getItem("orders")
    if (savedOrders) {
      try {
        const parsedOrders = JSON.parse(savedOrders)
        dispatch({ type: "FETCH_ORDERS_SUCCESS", payload: parsedOrders })
      } catch (error) {
        console.error("Failed to parse orders from localStorage:", error)
        localStorage.removeItem("orders")
        dispatch({ type: "FETCH_ORDERS_FAILURE", payload: error.message })
      }
    }
  }, [])

  // Save orders to localStorage whenever it changes
  useEffect(() => {
    if (state.orders.length > 0) {
      localStorage.setItem("orders", JSON.stringify(state.orders))
    }
  }, [state.orders])

  // Create a new order
  const createOrder = (orderData) => {
    // Generate a unique order ID
    const orderId = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`

    // Create order with status and timestamp
    const newOrder = {
      ...orderData,
      id: orderId,
      status: "processing",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    dispatch({ type: "ADD_ORDER", payload: newOrder })
    return orderId
  }

  // Get order by ID
  const getOrderById = (orderId) => {
    return state.orders.find((order) => order.id === orderId)
  }

  return (
    <OrderContext.Provider
      value={{
        ...state,
        createOrder,
        getOrderById,
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

// Custom hook to use the order context
export const useOrder = () => {
  const context = useContext(OrderContext)
  if (context === undefined) {
    throw new Error("useOrder must be used within an OrderProvider")
  }
  return context
}